import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { EntryLayout } from '../components/EntryLayout';
import { Icon } from '../components/Icon';
import { portalApi } from '../api/client';

type Phase = 'verifying' | 'error';

/**
 * Magic-link verify/landing route (`/verify?token=`) — US3.
 *
 * On mount, exchanges the single-use token for a portal session cookie, then
 * redirects to `/ideas` on success. The verify call is guarded so a re-mount
 * (StrictMode double-invoke, browser back/forward) never consumes the token a
 * second time (AC3.5). On failure shows an error state with a "Request a new
 * link" CTA (AC3.4).
 */
export function VerifyPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('verifying');
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return; // single-fire guard
    firedRef.current = true;

    const token = params.get('token');
    if (!token) {
      setPhase('error');
      return;
    }

    portalApi
      .verify(token)
      .then(() => {
        localStorage.setItem('portal_had_session', 'true');
        navigate('/ideas', { replace: true });
      })
      .catch(() => {
        setPhase('error');
      });
  }, [params, navigate]);

  if (phase === 'verifying') {
    return (
      <EntryLayout
        tagline="Signing you in…"
        description="Checking your access link."
      >
        <div className="text-center text-sm text-muted">Checking your link...</div>
      </EntryLayout>
    );
  }

  return (
    <EntryLayout
      tagline="Link expired"
      description="Access links are single-use and valid for 1 hour. Request a new link to continue."
    >
      <div className="mb-5 rounded-card border-[1.5px] border-[#FECACA] bg-[#FEF2F2] p-6 text-center">
        <div className="mx-auto mb-2.5 flex h-11 w-11 items-center justify-center rounded-full bg-[#FEE2E2] text-danger">
          <Icon name="clock" size={20} />
        </div>
        <div className="mb-1 font-bold text-danger">Link expired or already used</div>
        <div className="text-sm text-muted">
          Request a new link to continue.
        </div>
      </div>
      <Link
        to="/"
        className="block w-full rounded-[10px] bg-accent py-3 text-center text-sm font-bold text-white transition-colors hover:bg-accent-hover"
      >
        Request a new link
      </Link>
    </EntryLayout>
  );
}
